import { Injectable } from '@angular/core';
import { UserProfile } from '../interfaces/UserProfile';
import { Customer } from '../interfaces/customer';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map } from 'rxjs';
import { DateTime } from "luxon";
import { AuthenticationService } from './authentication.service';
import authHeader from '../helpers/auth-header';
import AuditDocument from '../interfaces/auditDocument';
import axios from 'axios';
import { CustomerAuditPenalty } from '../interfaces/customerAuditPenalty';

@Injectable({
  providedIn: 'root'
})
export class AuditService {

  constructor(private http: HttpClient, private authenticationService: AuthenticationService) { }

  walletURL: string = '';
  billingState: string = 'get';
  userProfile?: UserProfile;
  customer?: Customer;
  auditFiles: any = [];

  getAuditTransactions(): any {
    const config = this.getConfig();
       const url: string = `${environment.APP_API_URL}/api/v1/AuditCustomer/GetCurrentForCustomer`;
    return axios.post(url, {}, config)
        .then((response) => (
            response.data as any
        ))
        .catch((error) => {
            console.error(error);
            return {};
        });
}

  removeDocument(docId: string, authCode: string): any {
    if(authCode === undefined) {
        authCode = '';
    }

    const config = this.getConfig();
       const url: string = `${environment.APP_API_URL}/api/v1/AuditTransactionDocument/Delete?docId=${docId}&authCode=${authCode}`;
    return axios.post(url,{}, config)
        .then((response) => {
            if (response && response.status === 200) {
                return response.data;
            } else if (response && response.status === 401) {
                return 'unauthorized';
            } 
            return null;
        })
        .catch((error) => {
            console.error('Error uploading audit documents in service.')
            console.error(error);
            return null;
        });
}
  
  incrementUploadAttempts(authCode: string): any {
    const config = this.getConfig();
    const url: string = `${environment.APP_API_URL}/api/v1/AuditTransaction/IncrementUploadAttempts?authCode=${authCode}`;
    return axios.put(url, {}, config)
        .then((response) => {
            if (response && response.data) {
                return response.data as any;
            } 
            return -1;
        })
        .catch((error) => {
            console.error(error);
            return -1;
        });
}


  acknowledgeAudit(customerId: string): any {
    const config = this.getConfig();
    const url: string = `${environment.APP_API_URL}/api/v1/AuditCustomer/Acknowledge/${customerId}`;
    return axios.post(url, {}, config)
        .then(response => {
            return;
        })
        .catch(error => {
            console.error(`Error in acknowledgeAudit() for ${customerId}. ERROR: ${error}`);

            if (error.response.status === 401) {
                alert('Your session has expired. Please log in again.');
            }

            const errorMessage = error.response.statusText;
            return Promise.reject(errorMessage);
        });
}

  getFaxCoverSheet(authCode: string) {
    const config = this.getConfig();
    const url: string = `${environment.APP_API_URL}/api/v1/CoverSheet/GetCoverSheetFax?authCode=${authCode}`;

    return axios.post(url, {}, config);
}

  getProbationFaxCoversheet(firstName: string, lastName: string) {
    const config = this.getConfig();
    const url: string = `${environment.APP_API_URL}/api/v1/CoverSheet/GetCoverSheetFaxWithOptions?authCode=&firstName=${firstName}&lastName=${lastName}`;  

    return axios.post(url, {}, config);
}

  getProbationEmailLink(authCode: string) {
    const config = this.getConfig();
    const url: string = `${environment.APP_API_URL}/api/v1/CoverSheet/GetCoverSheetEmail?authCode=${authCode}`;

    return axios.post(url, {}, config)
        .then((response) => (
            response.data as any
        ))
        .catch((error) => {
            console.error(error);
            return {};
        });
}

     getCustomerAuditPenaltyStatus(): any {
          const userProfile = this.getUserProfile() as UserProfile;
          const customer = userProfile.customer as Customer;
          const auditPenalty: CustomerAuditPenalty | undefined = customer.auditPenalty;
          let auditPenaltyStatus: any = null;

          if (auditPenalty && auditPenalty.auditPenaltyActionId && auditPenalty.auditPenaltyActionId !== 0) {
               const penaltyStartDate: DateTime = DateTime.fromISO(auditPenalty.penaltyStartDate?.toString() as string);
               const goodStandingDate: DateTime = DateTime.fromISO(auditPenalty.customerGoodStandingDate?.toString() as string);
               const diff = goodStandingDate.diff(DateTime.now(), 'months');

               if (goodStandingDate >= DateTime.now()) {
                    auditPenaltyStatus = {
                         'penaltyDescription': auditPenalty.penaltyShortDescription,
                         'penaltyStartDate': penaltyStartDate.toLocaleString(),
                         'months': Math.trunc(diff.months),
                         'goodStandingDate': goodStandingDate.toLocaleString()
                    }
               }
          }
          return auditPenaltyStatus;
     }

  getUserProfile(): any {
      return this.authenticationService.getUserProfile();
  }

  uploadAuditDocuments(authCode: string, username: string, isProbation?: boolean): any {
    if (this.auditFiles.length === 0) {
        return null;
    }

    const config = {
        headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': authHeader()
        }, validateStatus: (status: any) => {
            return status;
        }
    };
    const payload = new FormData();
    payload.append('authCode', authCode);
    payload.append('username', username);

    for (let i = 0; i < this.auditFiles.length; i++) {
        payload.append(i.toString(), this.auditFiles[i]);
    }

       let url = `${environment.APP_API_URL}/api/v1/AuditTransactionDocument/Upload`;
    if (typeof isProbation !== 'undefined') {
        url += `?isProbation=${isProbation}`;
    }
    return axios.post(url, payload, config)
        .then((response) => {
            if (response && response.status === 200) {
                return response.data;
            } else if (response && response.status === 401) {
                return 'unauthorized';
            }
            return null;
        })
        .catch((error) => {
            console.error('Error uploading audit documents in service.')
            console.error(error);
            return null;
        });
  }
  
  deleteProbationDocument(docId: string): any {
      this.auditFiles = this.auditFiles.filter((d: any) => d.docId !== docId);
      return this.removeDocument(docId, '')
  }

  updateProbationTransactionStatus(authCode: string, statusId: number): any {
    const config = this.getConfig();
    const payload: any = {
        'authCode': authCode,
        'newStatus': statusId 
    }

    const url = `${environment.APP_API_URL}/api/v1/Probation/UpdateProbationTransactionStatus/?authCode=${authCode}&newStatus=${statusId}`;
    return axios.post(url, {}, config)
        .then((response) => (
            response.data as any
        ))
        .catch((error) => {
            console.error(error);
            return {};
        });
  }

  sendProbationUploadEmail(driverFirstName: string, driverLastName: string, requestDate: string): any {
    const config = this.getConfig();

    const url = `${environment.APP_API_URL}/api/v1/Probation/sendProbationUploadEmail/?driverFirstName=${driverFirstName}&driverLastName=${driverLastName}&requestDate=${requestDate}`;
    return axios.post(url, {}, config)
        .then((response) => {
            if (response) {
                return response.data;
            }
            return null;
        })
        .catch((error) => {
            console.error(error);
            return null;
        });
  }

  private getConfig() {
      return {
          headers: {
              'Content-Type': 'application/json;charset=UTF-8',
              'Authorization': authHeader()
          }
      };
  }

  mockGetAuditTransactions(): any {
      return this.mockAuditTransactions;
  }

  mockAuditTransactions: any = {
      "Id": null,
      "AuditId": "C7029955B870B93CE0530AAE1AACA6C7",
      "CustomerId": 10006,
      "AuditCustomerStatus": "Acknowledged",
      "AuditCustomerStatusId": 3,
      "AcknowledgedDate": "\\/Date(1631640507000)\\/",
      "CustomerName": "QA MC Trucking - SoapUI",
      "AuditTransactions": [
          {
              "Id": "C7029955B874B93CE0530AAE1AACA6C7",
              "AuditId": "C7029955B870B93CE0530AAE1AACA6C7",
              "CustomerId": 0,
              "AuthCode": "27aa90c8-7b6b-4044-ba17-ac75a0d8d4ea",
              "Status": "Open",
              "StatusId": 1,
              "RejectionReasonCodes": [],
              "RequestDate": "\\/Date(1398705786000)\\/",
              "LastName": "CHROME",
              "FirstName": "FOUR",
              "DlNum": "TX123456",
              "DlState": "TX",
              "Requestor": "mainlineuser4@gmail.com",
              "CreatedBy": null,
              "CreatedDate": "\\/Date(-62135578800000)\\/",
              "UpdatedBy": null,
              "UpdatedDate": "\\/Date(-62135578800000)\\/",
              "Documents": [],
              "DocUploadAttempts": 0
          },
          {
              "Id": "C7029955B873B93CE0530AAE1AACA6C7",
              "AuditId": "C7029955B870B93CE0530AAE1AACA6C7",
              "CustomerId": 0,
              "AuthCode": "3a65d774-f808-400d-858e-811ef030e0a7",
              "Status": "Open",
              "StatusId": 1,
              "RejectionReasonCodes": [],
              "RequestDate": "\\/Date(1398704778000)\\/",
              "LastName": "CHROME",
              "FirstName": "TEST",
              "DlNum": "CHROME12345",
              "DlState": "AL",
              "Requestor": "testb02@hotmail.com",
              "CreatedBy": null,
              "CreatedDate": "\\/Date(-62135578800000)\\/",
              "UpdatedBy": null,
              "UpdatedDate": "\\/Date(-62135578800000)\\/",
              "Documents": [],
              "DocUploadAttempts": 0
          },
          {
              "Id": "C7029955B872B93CE0530AAE1AACA6C7",
              "AuditId": "C7029955B870B93CE0530AAE1AACA6C7",
              "CustomerId": 0,
              "AuthCode": "dc9c1f86-a716-4c73-b6db-63d084dbee21",
              "Status": "Open",
              "StatusId": 1,
              "RejectionReasonCodes": [],
              "RequestDate": "\\/Date(1398705235000)\\/",
              "LastName": "REQUESTDIRPAGE",
              "FirstName": "TEST",
              "DlNum": "REQ123456",
              "DlState": "TN",
              "Requestor": "testb02@hotmail.com",
              "CreatedBy": null,
              "CreatedDate": "\\/Date(-62135578800000)\\/",
              "UpdatedBy": null,
              "UpdatedDate": "\\/Date(-62135578800000)\\/",
              "Documents": [],
              "DocUploadAttempts": 0
          }
      ],
      "PrimaryContactFirstName": "Robert",
      "PrimaryContactLastName": "Taff",
      "PrimaryContactPhone": "(303) 256-9300 x",
      "CreatedBy": null,
      "CreatedDate": "\\/Date(1626188165000)\\/",
      "UpdatedBy": null,
      "UpdatedDate": "\\/Date(-62135578800000)\\/",
      "AuditDueDate": "\\/Date(1627484165000)\\/",
      "AuditChangeLogs": [
          {
              "ChangeId": "CBF8E8E16FC93638E0530AAE1AAC957C",
              "EntityRecordId": "C7029955B871B93CE0530AAE1AACA6C7",
              "EntityField": "AUDIT_ACKNOWLEDGE_DATE",
              "OldValue": null,
              "NewValue": "14-sep-2021 12:28:27",
              "ChangedBy": "robert.taff@mailinator.com",
              "ChangedDate": "\\/Date(1631644108000)\\/"
          },
          {
              "ChangeId": "CBF8E8E16FC83638E0530AAE1AAC957C",
              "EntityRecordId": "C7029955B871B93CE0530AAE1AACA6C7",
              "EntityField": "AUDIT_STATUS",
              "OldValue": "1",
              "NewValue": "3",
              "ChangedBy": "robert.taff@mailinator.com",
              "ChangedDate": "\\/Date(1631644108000)\\/"
          }
      ],
      "PrimaryContactEmail": "robert.taff@mailinator.com",
      "AuditPeriodStartDate": "\\/Date(1396328400000)\\/",
      "AuditPeriodEndDate": "\\/Date(1398834000000)\\/",
      "AuditCloseDate": "\\/Date(1627484165000)\\/",
      "AuditMasterStatus": "Open",
      "RespondedToAudit": false,
      "Status": 0,
      "Message": null
  };



}
